import { useQuery } from "@tanstack/react-query";
import React, { FC, useCallback, useEffect, useState } from "react";
import TableComponent from "src/core/components/table/table.component";
import CustomBarChartComponent from "src/shared/custom-highchart-component/custom-bar-chart.component";
import { generateFilenameWithTimestamp } from "src/shared/helpers/generateFilenameWithTimestamp";
import TableSort from "src/shared/helpers/table-sort";
import { getMaintenanceData } from "../../../../../core/api/services/dashbaorad-details/burn-time.service";
import ModalComponent from "../../../../../core/components/modal/modal.component";
import CustomSpinnerComponent from "../../../../../shared/custom-spinner/custom-spinner.component";

interface BurnTimeMaintenanceChartTypes {
  blockCode: any;
  isRefreshed: boolean;
}

const BurnTimeMaintenanceChart: FC<BurnTimeMaintenanceChartTypes> = ({
  blockCode,
  isRefreshed,
}) => {
  const [maintenanceChartData, setMaintenanceChartData] = useState<any>([]);
  const [assetData, setAssetData] = useState([]);
  const [modalTitle, setModalTitle] = useState<string>("");
  const pageSize = 10;
  const [pageNo, setPageNo] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [visibleAssetData, setVisibleAssetData] = useState([]);
  const [isSortInitiated, setIsSortInitiated] = useState<boolean>(false);

  const [columns, setColumns] = useState([
    {
      title: "Display Name",
      label: "assetDisplayName",
      type: "text",
      width: "14rem",
    },
    {
      title: "Asset ID",
      label: "assetCode",
      isSorted: true,
      // width: "22rem",
      type: "link",
      link: "/asset",
      linkableField: "assetCode",
    },
  ]);

  const emptyConfig = {
    title: "No data found",
    description: "",
  };

  // Sort table data on initial load
  useEffect(() => {
    if (!isSortInitiated && columns.length > 0 && assetData.length > 0) {
      const sortBy = columns[0].label; // Get the label of the first column
      const sortedData = [...assetData].sort((a: any, b: any) => {
        const valueA = a[sortBy];
        const valueB = b[sortBy];

        if (valueA < valueB) {
          return 1; // Descending order
        }
        if (valueA > valueB) {
          return -1; // Descending order
        }
        return 0;
      });

      setAssetData(sortedData); // Update the sorted assetData
      calculatePaginationItems(pageNo, sortedData); // Recalculate pagination items
      setIsSortInitiated(true); // Mark sorting as initiated
    }
  }, [columns, assetData, isSortInitiated]); // Add isSortInitiated as a dependency

  const onChangeSort = useCallback(
    async (sortBy: string, sortDirection: string) => {
      if (!sortBy || !sortDirection) return;

      setIsSortInitiated(true); // Set the sort initiated state to true

      // Lowercase the first character of sortBy
      sortBy = sortBy?.charAt(0).toLowerCase() + sortBy?.slice(1);

      // Sort the assetData based on sortBy and sortDirection
      const sortedData = [...assetData].sort((a: any, b: any) => {
        const valueA = a[sortBy];
        const valueB = b[sortBy];

        if (valueA < valueB) {
          return sortDirection === "asc" ? -1 : 1;
        }
        if (valueA > valueB) {
          return sortDirection === "asc" ? 1 : -1;
        }
        return 0;
      });

      setAssetData(sortedData); // Update the sorted assetData

      // Recalculate pagination items based on the sorted data
      calculatePaginationItems(pageNo, sortedData);

      // Update the column sorting state
      const copyColumns: any = await TableSort(columns, sortBy);
      setColumns(copyColumns);
    },
    [assetData, columns, pageNo]
  );

  const { data: maintenanceData, isLoading: isLoadingMaintenanceData } =
    useQuery({
      queryKey: ["getMaintenanceData", blockCode],
      queryFn: ({ signal }) => getMaintenanceData(blockCode, signal),
      enabled: !!blockCode || isRefreshed,
    });

  const onClickPieSlice = (event: any) => {
    const matchedCategory = maintenanceData?.data?.find(
      (item: any) => item.burnTimeUsage === event?.point?.category
    );

    // const assetCodes = matchedCategory ? matchedCategory.assetCodes : [];
    const assets = matchedCategory ? matchedCategory.assets : [];
    //  ;

    setAssetData(assets);
    // setAssetCodes(assetCodes);
    setModalTitle(`Assets with ${event?.point?.category}`);
    calculatePaginationItems(pageNo, assets);
    setIsModalOpen(true);
  };

  const calculatePaginationItems = (page: any, assetDataP?: any) => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    let visibleAssetData;
    if (!assetDataP) {
      visibleAssetData = assetData.slice(startIndex, endIndex);
    } else {
      visibleAssetData = assetDataP.slice(startIndex, endIndex);
    }

    setVisibleAssetData(visibleAssetData);
  };

  const onChangePage = (page: number) => {
    setPageNo(page);
    calculatePaginationItems(page);
  };

  useEffect(() => {
    setPageNo(1);
  }, [isModalOpen]);

  useEffect(() => {
    if (maintenanceData?.data?.length) {
      const formattedData = maintenanceData?.data?.every(
        (item: any) =>
          item?.burnTimeUsage == null || item?.luminarieQuantity == null
      )
        ? null
        : maintenanceData?.data?.map((item: any) => ({
          name: item.burnTimeUsage,
          y: item.luminarieQuantity,
        }));

      const reversedData = formattedData?.slice().reverse();

      setMaintenanceChartData(reversedData);
    }
  }, [maintenanceData]);

  const handleClose = () => {
    setIsModalOpen(false); // Close the modal
    setIsSortInitiated(false); // Reset the sort initiated state
    setAssetData([]); // Reset the table data
    setVisibleAssetData([]); // Reset the visible data for pagination
    setPageNo(1); // Reset the page number
  };

  return (
    <>
      <div className="w-lg-48 w-100">
        <div className="bg-card">
          <h4
            className="h4"
            style={{
              marginLeft: "1rem",
              zIndex: 999,
              position: "relative",
            }}
          >
            Maintenance Chart{" "}
            <span style={{ fontSize: "16px" }}> (Based on 50,000 hrs)</span>
          </h4>

          <CustomSpinnerComponent
            height={"400px"}
            width={"100%"}
            isLoading={isLoadingMaintenanceData || isRefreshed}
          >
            <CustomBarChartComponent
              data={maintenanceChartData}
              onClickSlice={onClickPieSlice}
              fileName={generateFilenameWithTimestamp(
                "Maintenance Chart (Based on 50,000 hrs)"
              )}
              chartName={"Maintenance Chart (Based on 50,000 hrs)"}
            />
          </CustomSpinnerComponent>
        </div>
      </div>
      {isModalOpen && (
        <ModalComponent
          showModal={isModalOpen}
          handleClose={handleClose}
          headerTitle={modalTitle}
          isViewMode={true}
        >
          <TableComponent
            tableHeaders={columns}
            emptyConfig={emptyConfig}
            data={visibleAssetData}
            totalNumberOfRecords={assetData?.length}
            pageSize={pageSize}
            current={pageNo}
            onChangePage={onChangePage}
            onChangeSort={onChangeSort}
          />
        </ModalComponent>
      )}
    </>
  );
};

export default React.memo(BurnTimeMaintenanceChart);
